import React, { useMemo, type JSX } from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { useStateContext } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import { EDITING } from 'app-components/flux/transaction-rules/transactionRulesTypes';

import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  row: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  leftButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  rightButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  errorTooltip: {
    background: 'transparent',
  },
  errorTooltipContent: {
    fontSize: theme.typography.paragraphRegular.fontSize,
    fontWeight: theme.typography.paragraphRegular.fontWeight,
    background: theme.palette.error.light,
    color: theme.palette.error.dark,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    borderRadius: theme.shape.borderRadius * 3,
    '> svg': {
      height: 16,
      width: 16,
      marginRight: theme.spacing(0.5),
    },
  },
}));

const RuleFooter: React.FC<{ onSubmit: () => void; onCancel: () => void; onDelete: () => void }> = ({
  onSubmit,
  onCancel,
  onDelete,
}): JSX.Element => {
  const { classes } = useStyles();
  const { state } = useStateContext();
  const { stage, buttonText, buttonHelperText, error, rule } = state;

  const buttonEndIcon = stage === EDITING ? <ArrowForwardIcon /> : undefined;

  const createMarkup = useMemo(() => {
    return {
      __html: buttonHelperText.includes('existing & future')
        ? buttonHelperText.replace(
            /existing & future/g,
            '<span style="font-weight: 500; font-size: 14px;">existing & future</span>',
          )
        : buttonHelperText.includes('future')
          ? buttonHelperText.replace(/future/g, '<span style="font-weight: 500; font-size: 14px;">future</span>')
          : buttonHelperText,
    };
  }, [buttonHelperText]);

  return (
    <Box className={classes.row}>
      <Box className={classes.leftButtons}>
        {rule?.id && stage === EDITING && (
          <Button id='rule-form-edit-button' color='error' onClick={onDelete}>
            Delete Rule
          </Button>
        )}
      </Box>

      <Box className={classes.rightButtons}>
        <Typography variant='paragraphRegular'>
          <span dangerouslySetInnerHTML={createMarkup} />
        </Typography>

        <Button size='large' id='rule-form-cancel-button' onClick={onCancel}>
          Cancel
        </Button>

        <Tooltip
          classes={{ tooltip: classes.errorTooltip }}
          title={
            <Box className={classes.errorTooltipContent}>
              <ErrorIcon />
              <Typography variant='paragraphRegular' component='span'>
                {error}
              </Typography>
            </Box>
          }
          open={!!error}
          arrow
        >
          <Button
            size='large'
            id='rule-form-submit-button'
            variant='contained'
            onClick={onSubmit}
            endIcon={buttonEndIcon}
          >
            {buttonText}
          </Button>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default RuleFooter;
